// react
import { useEffect, useRef, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  Dialog,
  Grid,
  IconButton,
  lighten,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';
import { downLoadCsvFile } from '../../../../utils/export';
import { getTypeColor } from '../../../../utils/powerSource';
import EsgDistributionStackedLineChart from './EsgDistributionStackedLineChart';
import GridMixDatagrid from './GridMixDatagrid';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/full-screen';

const EsgDistributionPieChart = ({
  GridMixLocationSummary = [],
  GridMixData,
  timeRange,
}) => {
  const { t } = useTranslation('energyxplorer-esg');
  const theme = useTheme();
  const chartComponentRef = useRef(null);

  const [distributionToggle, setDistributionToggle] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState([]);

  // Prepare chart data based on energyDistributionSummary
  const chartData =
    GridMixLocationSummary &&
    GridMixLocationSummary.map((item) => ({
      name: item.Name,
      y: item.ContributionPercentage,
      color: lighten(getTypeColor(item.Name), 0.2),
    }));

  const options = {
    chart: {
      type: 'pie',
    },
    title: {
      text: null,
    },
    tooltip: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        innerSize: '0%',
        dataLabels: {
          enabled: true,
          formatter: function () {
            return `<span style="font-size:14px; font-weight:normal;">${
              this.point.name
            }: ${formatNumber(this.point.y)}%</span>`;
          },
        },
      },
      series: {
        allowPointSelect: true,
        cursor: 'pointer',
        point: {
          events: {
            select: function (e) {
              setSelectedPoints((prev) => [...prev, e.target.name]);
            },
            unselect: function (e) {
              setSelectedPoints((prev) =>
                prev.filter((o) => o != e.target.name),
              );
            },
          },
        },
      },
    },
    series: [
      {
        name: t('energy_distribution_pie_chart.label'),
        data: chartData,
      },
    ],
  };

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: {
            backgroundColor: themeOptions.chart.backgroundColor,
          },
          time: {
            // timezoneOffset: new Date().getTimezoneOffset('da-DK'),
            useUTC: true,
          },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black', // ✅ Dynamic text color
            },
          },
          plotOptions: {
            pie: {
              borderColor: theme.palette.mode === 'light' ? 'white' : 'black',
              dataLabels: {
                style: {
                  color: theme.palette.mode === 'dark' ? 'white' : 'black', // ✅ Dynamic text color
                },
              },
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);

  const handleToggleFullscreen = () => {
    chartComponentRef.current.chart.fullscreen.open();
  };

  return (
    <>
      <Grid
        size={{
          md: 12,
          lg: 6
        }}>
        <Card sx={{ borderRadius: 2, minHeight: 260, height: '100%' }}>
          <CardContent sx={{ height: '100%' }}>
            <Stack
              direction="column"
              color="inherit"
              // component={Card}
              elevation={0}
              sx={{
                transition: 'box-shadow 0.3s ease',
                height: '100%',
              }}
            >
              <Stack
                spacing={1}
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      fontWeight="bold"
                    >
                      {t('energy_distribution_pie_chart.label')}
                    </Typography>
                    <InfoTooltipIcon
                      title={t('energy_distribution_pie_chart.description')}
                    />
                  </Box>
                  <Box className="non-printable">
                    <Tooltip
                      title={t('energy_distribution_pie_chart.download_as_csv')}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          downLoadCsvFile(
                            [
                              ...chartData.map((o) => ({
                                source: o.name,
                                percentage: o.y,
                              })),
                            ],
                            'power_source_data',
                          )
                        }
                        aria-label="slice-mode-selector"
                        sx={{ borderRadius: 1 }}
                      >
                        <FileDownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => setDistributionToggle(true)}
                      aria-label="slice-mode-selector"
                      sx={{ borderRadius: 1 }}
                    >
                      <OpenInFullRoundedIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Box
                  display="flex"
                  justifyContent="center"
                  width="100%"
                  height="100%"
                  sx={{ position: 'relative' }}
                >
                  <IconButton
                    className="non-printable"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 2,
                    }}
                    onClick={() => handleToggleFullscreen()}
                  >
                    <FullscreenIcon />
                  </IconButton>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={options}
                    ref={chartComponentRef}
                    containerProps={{
                      style: { width: '100%', height: '100%', minHeight: 400 },
                    }}
                  />
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid
        size={{
          md: 12,
          lg: 6
        }}>
        <GridMixDatagrid
          gridMixData={chartData}
          selectedList={selectedPoints}
        />
      </Grid>

      <Dialog
        maxWidth="lg"
        open={distributionToggle}
        onClose={() => setDistributionToggle(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '90vw',
            maxWidth: 'none',
          },
        }}
      >
        <EsgDistributionStackedLineChart
          GridMixData={GridMixData}
          timeRange={timeRange}
        />
      </Dialog>
    </>
  );
};

export default EsgDistributionPieChart;
